/**
 * Vendor Portal Hooks
 * Scoped data access for cross-org vendor users.
 * Vendors are identified via vendors.linked_org_id matching the user's organization_id.
 * This means the user's org is listed as a vendor in another org's vendor list.
 * Supports optional vendorIdOverride for admin preview mode.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/stores'
import type { Vendor } from '@/types'

// Shape returned by get_vendor_portal_bid_items_v2 RPC.
// Keeps v1-compat alias keys (actual_cost, quote_file_path, bid_status, etc.) so
// existing UI components keep working without per-component v2 mapping.
export interface VendorBidItem {
  id: string
  source: 'v2'
  work_order_id: string | null
  work_order_task_id: string
  vendor_id: string
  title: string | null
  description: string | null
  estimated_cost: number | null
  actual_cost: number | null
  total_amount: number | null
  bid_status: string | null
  bid_file_path: string | null
  bid_file_name: string | null
  quote_file_path: string | null
  quote_file_name: string | null
  invoice_file_path: string | null
  invoice_file_name: string | null
  final_invoice_amount: number | null
  scheduled_date: string | null
  completion_photos: string[] | null
  work_completed_at: string | null
  vendor_notes: string | null
  bid_submitted_at: string | null
  bid_declined_at: string | null
  bid_decline_reason: string | null
  is_awarded: boolean | null
  awarded_at: string | null
  created_at: string | null
  updated_at: string | null
  /** Sender closed the job (completed/invoiced/cancelled WO or finished line item). */
  job_closed?: boolean
  vendor: { id: string; name: string } | null
  parent_task: {
    id: string
    title: string | null
    task_number: number | null
    scope_of_work: string | null
  } | null
  work_order: {
    id: string
    work_order_number: string | null
    title: string | null
    priority: string | null
    scheduled_start: string | null
    scheduled_end: string | null
    organization_id: string
  } | null
  scope_items: Array<{
    id: string
    title: string | null
    task_number: number | null
    scope_of_work: string | null
    completion_photos: string[] | null
    archived_at: string | null
  }> | null
}

/**
 * Vendor-facing status, derived only from the vendor's own bid. The sender's
 * internal WO status (invoiced_100, waiting_on_fc, ...) is never shown here —
 * payment state will come from payment tracking once that exists.
 */
export type VendorJobStatus = 'invited' | 'submitted' | 'awarded' | 'work_complete' | 'job_closed' | 'declined'

export function vendorJobStatus(item: VendorBidItem): VendorJobStatus {
  const s = item.bid_status
  if (s === 'rejected' || s === 'declined' || item.bid_declined_at) return 'declined'
  if (item.is_awarded || s === 'accepted') {
    if (item.work_completed_at) return 'work_complete'
    return item.job_closed ? 'job_closed' : 'awarded'
  }
  if (s === 'submitted' || item.bid_submitted_at) return 'submitted'
  return 'invited'
}

export const VENDOR_JOB_STATUS_LABEL: Record<VendorJobStatus, string> = {
  invited: 'Invited',
  submitted: 'Bid submitted',
  awarded: 'Awarded',
  work_complete: 'Work complete',
  job_closed: 'Job closed',
  declined: 'Declined',
}

export const VENDOR_JOB_STATUS_CLASS: Record<VendorJobStatus, string> = {
  invited: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  submitted: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  awarded: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
  work_complete: 'bg-green-500/10 text-green-600 border-green-500/20',
  job_closed: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  declined: 'bg-red-500/10 text-red-600 border-red-500/20',
}

export function useVendorPortalBidItems() {
  const user = useAuthStore((s) => s.user)
  return useQuery({
    queryKey: ['vendor-portal', 'bid-items', user?.organization_id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_vendor_portal_bid_items_v2')
      if (error) throw error
      return (data ?? []) as unknown as VendorBidItem[]
    },
    enabled: !!user?.organization_id,
  })
}

/**
 * Resolve vendor IDs that represent the current user's org in other orgs.
 * A vendor record with linked_org_id = user.organization_id means
 * "this vendor entry in another org's list points to the user's org."
 */
export function useMyVendorIds() {
  const user = useAuthStore((s) => s.user)
  const orgId = user?.organization_id

  return useQuery({
    queryKey: ['vendor-portal', 'my-vendor-ids', orgId],
    queryFn: async () => {
      if (!orgId) return []
      const { data, error } = await supabase
        .from('vendors')
        .select('id, organization_id, name')
        .eq('linked_org_id', orgId)
      if (error) throw error
      return data ?? []
    },
    enabled: !!orgId,
  })
}

export function useVendorPortalProfile(vendorIdOverride?: string) {
  const { data: myVendors } = useMyVendorIds()
  const vendorId = vendorIdOverride ?? myVendors?.[0]?.id

  return useQuery({
    queryKey: ['vendor-portal', 'profile', vendorId],
    queryFn: async () => {
      if (!vendorId) return null

      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('id', vendorId)
        .single()

      if (error) throw error
      return data as Vendor
    },
    enabled: !!vendorId,
  })
}
